import {
  calculateProductRow, normalizeProductConfig, normalizeProductName,
  productSupportsLining, resolveEffectiveQuoteCalculationSettings,
  CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION,
} from "./productConfigs";
import { calculateCurtainQuoteTotals, num, round2 } from "./curtainQuoteTotals";
import { getQuoteFabricIdentity } from "./quoteCalculationSettings";
import { cuttingDropMetres } from "./patternRepeat";

// Financial primitives extracted verbatim from QuotationApp. Keep the app on
// these exports when migrating it, rather than maintaining two implementations.
const sanitizeEditableText = (value) => {
  const text = String(value ?? "").trim();
  const lowered = text.toLowerCase();
  if (text === "\u2014" || text === "\u2013") return "";
  if (["\u2014", "-", "n/a", "na"].includes(text) || ["n/a", "na"].includes(lowered)) return "";
  return text;
};

const DEFAULT_STITCHING = {
  "3 Pleat - M Track": {
    rate: 330,
    fullness: 3,
    trackType: "M Track",
    trackLengthFt: 16,
    trackRateFt: 0, // user can edit in Stitching tab
  },
  "Ripple - Arabian Track": {
    rate: 470,
    fullness: 3,
    trackType: "Arabian Track",
    trackLengthFt: 16,
    trackRateFt: 0,
  },
  "Ripple - Jumbo Track": {
    rate: 550,
    fullness: 3,
    trackType: "Jumbo Track",
    trackLengthFt: 16,
    trackRateFt: 0,
  },
  "Eyelet (Custom)": {
    rate: 0,
    fullness: 3,
    trackType: "",
    trackLengthFt: 0,
    trackRateFt: 0,
  },
};



const CURTAIN_LINING_TYPES = [
  { type: "satin", label: "Satin" },
  { type: "dimout", label: "Dimout" },
  { type: "blackout", label: "Blackout" },
  { type: "custom", label: "Custom" },
];

const DEFAULT_CURTAIN_LINING = {
  enabled: false,
  type: "satin",
  label: "Satin",
  customLabel: "",
  chargeMethod: "meter",
  ratePerMeter: "",
  ratePerSqft: "",
  fabricCode: "",
  brandId: "",
  brandName: "",
  catalogueId: "",
  catalogueName: "",
  srNumber: "",
  metersMode: "same_as_curtain",
  calculatedMeters: 0,
  meters: 0,
  manualMeters: null,
  overrideMeters: "",
  billableMeters: 0,
  sqft: 0,
  includedInSqftRate: false,
  notes: "",
  amount: 0,
};

const getCurtainLiningTypeMeta = (type = "") =>
  CURTAIN_LINING_TYPES.find((entry) => entry.type === type) ||
  CURTAIN_LINING_TYPES[0];

const parseNonNegativeNumberInput = (value, fallback = 0) => {
  if (value === "" || value == null) return fallback;
  const numeric = Number(String(value).replace(/,/g, "").trim());
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(0, numeric);
};

const getCurtainLiningLabel = (lining = {}) => {
  const type = String(lining?.type || "satin").trim().toLowerCase();
  if (type === "custom") {
    return sanitizeEditableText(lining?.customLabel || lining?.label) || "Custom lining";
  }
  return getCurtainLiningTypeMeta(type).label;
};

const normalizeCurtainRowLining = (
  lining = {},
  { calculatedMeters = 0, sqft = 0, applicable = true } = {}
) => {
  const safeCalculatedMeters = round2(Math.max(0, Number(calculatedMeters || 0)));
  const safeSqft = round2(Math.max(0, Number(sqft || 0)));
  const rawType = String(lining?.type || "satin").trim().toLowerCase();
  const type = CURTAIN_LINING_TYPES.some((entry) => entry.type === rawType)
    ? rawType
    : "satin";
  const chargeMethod = lining?.chargeMethod === "sqft" ? "sqft" : "meter";
  const hasManualMeterValue =
    lining?.manualMeters !== null &&
    lining?.manualMeters !== undefined &&
    String(lining.manualMeters).trim() !== "";
  const hasOverrideMeterValue =
    lining?.overrideMeters !== null &&
    lining?.overrideMeters !== undefined &&
    String(lining.overrideMeters).trim() !== "";
  const hasCustomMeterValue =
    lining?.customMeters !== null &&
    lining?.customMeters !== undefined &&
    String(lining.customMeters).trim() !== "";
  const hasLegacyManualOverrideMeters =
    lining?.metersMode !== "same_as_curtain" &&
    (
      (hasManualMeterValue && parseNonNegativeNumberInput(lining.manualMeters, 0) > 0) ||
      (hasOverrideMeterValue && parseNonNegativeNumberInput(lining.overrideMeters, 0) > 0) ||
      (hasCustomMeterValue && parseNonNegativeNumberInput(lining.customMeters, 0) > 0)
    );
  const metersMode =
    lining?.metersMode === "custom_override" ||
    hasLegacyManualOverrideMeters
      ? "custom_override"
      : "same_as_curtain";
  const rawMeterRate =
    lining?.ratePerMeter ?? lining?.rate ?? lining?.defaultRatePerMeter ?? "";
  const rawSqftRate =
    lining?.ratePerSqft ?? lining?.sqftRate ?? lining?.defaultRatePerSqft ?? "";
  const rawOverride =
    lining?.manualMeters ?? lining?.overrideMeters ?? lining?.customMeters ?? "";
  const ratePerMeter = parseNonNegativeNumberInput(rawMeterRate, 0);
  const ratePerSqft = parseNonNegativeNumberInput(rawSqftRate, 0);
  const overrideMeters = parseNonNegativeNumberInput(rawOverride, 0);
  const enabled = applicable && lining?.enabled === true;
  const billableMeters =
    enabled && metersMode === "custom_override"
      ? round2(overrideMeters)
      : enabled
      ? safeCalculatedMeters
      : 0;

  const amount = enabled
    ? chargeMethod === "sqft"
      ? round2(safeSqft * ratePerSqft)
      : round2(billableMeters * ratePerMeter)
    : 0;

  const normalized = {
    ...DEFAULT_CURTAIN_LINING,
    ...lining,
    enabled,
    type,
    label: getCurtainLiningLabel({ ...lining, type }),
    customLabel: sanitizeEditableText(lining?.customLabel || ""),
    chargeMethod,
    ratePerMeter: rawMeterRate === null || rawMeterRate === undefined ? "" : rawMeterRate,
    ratePerSqft: rawSqftRate === null || rawSqftRate === undefined ? "" : rawSqftRate,
    fabricCode: sanitizeEditableText(lining?.fabricCode || lining?.fabric || ""),
    brandId: sanitizeEditableText(lining?.brandId || lining?.fabricBrandId || ""),
    brandName: sanitizeEditableText(lining?.brandName || lining?.fabricBrandName || ""),
    metersMode,
    calculatedMeters: safeCalculatedMeters,
    meters: billableMeters,
    manualMeters: metersMode === "custom_override" ? billableMeters : null,
    overrideMeters: rawOverride === null || rawOverride === undefined ? "" : rawOverride,
    billableMeters,
    sqft: safeSqft,
    includedInSqftRate: lining?.includedInSqftRate === true,
    notes: sanitizeEditableText(lining?.notes || ""),
    amount,
  };

  return normalized;
};

const calculateCurtainRowLining = (
  row = {},
  billableMeters = 0,
  applicable = true,
  options = {}
) =>
  normalizeCurtainRowLining(row?.lining || {}, {
    calculatedMeters: billableMeters,
    sqft: options.sqft ?? options.adjSqft ?? row?.adjSqft ?? 0,
    applicable,
  });


const BENDED_TRACK_PRICING_METHODS = [
  { value: "none", label: "No extra bend charge" },
  { value: "fixed_per_bend", label: "Fixed charge per bend" },
  { value: "by_angle", label: "Charge by bend angle" },
  { value: "manual", label: "Manual charge in quote" },
];

const BENDED_TRACK_SHAPES = [
  { shape: "straight", label: "Straight Track", defaultBendCount: 0, defaultAngleDeg: null },
  { shape: "bend_90", label: "90 deg L Bend", defaultBendCount: 1, defaultAngleDeg: 90, angleKey: "90" },
  { shape: "bend_120", label: "120 deg Bend", defaultBendCount: 1, defaultAngleDeg: 120, angleKey: "120" },
  { shape: "bend_135", label: "135 deg Bend", defaultBendCount: 1, defaultAngleDeg: 135, angleKey: "135" },
  { shape: "u_shape", label: "U Shape / Two Bends", defaultBendCount: 2, defaultAngleDeg: null, angleKey: "custom" },
  { shape: "custom", label: "Custom Angle", defaultBendCount: 1, defaultAngleDeg: null, angleKey: "custom" },
];

const BENDED_TRACK_SHAPE_MAP = Object.fromEntries(
  BENDED_TRACK_SHAPES.map((entry) => [entry.shape, entry])
);

const DEFAULT_BEND_SETTINGS = {
  enabled: false,
  pricingMethod: "none",
  defaultBendCharge: 0,
  angleCharges: {
    90: 0,
    120: 0,
    135: 0,
    custom: 0,
  },
  allowedAngles: ["bend_90", "bend_120", "bend_135", "u_shape", "custom"],
  notes: "",
};

const DEFAULT_TRACK_CONFIG = {
  shape: "straight",
  isBended: false,
  angleDeg: null,
  bendCount: 0,
  bendChargePerBend: 0,
  bendAmount: 0,
  pricingMethod: "none",
  manualBendCharge: null,
  notes: "",
  segmentUnit: "in",
  segments: [
    { label: "A", length: 0, unit: "in" },
    { label: "B", length: 0, unit: "in" },
    { label: "C", length: 0, unit: "in" },
  ],
  totalSegmentLength: 0,
  totalSegmentLengthUnit: "in",
  totalTrackLengthFt: 0,
  bendLocations: [],
  bendLocationSummary: "",
};

const parseStitchingSettingNumber = (value, fallback = 0) => {
  if (value === "" || value == null) return fallback;
  const numeric = Number(String(value).replace(/,/g, "").trim());
  return Number.isFinite(numeric) ? numeric : Number.NaN;
};

const normalizeBendedTrackShape = (shape = "straight") => {
  const normalizedShape = String(shape || "straight").trim();
  if (BENDED_TRACK_SHAPE_MAP[normalizedShape]) return normalizedShape;
  if (normalizedShape === "90" || normalizedShape === "bend90") return "bend_90";
  if (normalizedShape === "120" || normalizedShape === "bend120") return "bend_120";
  if (normalizedShape === "135" || normalizedShape === "bend135") return "bend_135";
  if (normalizedShape === "u" || normalizedShape === "u-shape") return "u_shape";
  return "straight";
};

const normalizeAllowedBendShapes = (allowedAngles = DEFAULT_BEND_SETTINGS.allowedAngles) => {
  const source = Array.isArray(allowedAngles) ? allowedAngles : DEFAULT_BEND_SETTINGS.allowedAngles;
  const allowed = source
    .map((entry) => {
      if (entry === 90 || entry === "90") return "bend_90";
      if (entry === 120 || entry === "120") return "bend_120";
      if (entry === 135 || entry === "135") return "bend_135";
      if (entry === "u" || entry === "u_shape" || entry === "u-shape") return "u_shape";
      if (entry === "custom") return "custom";
      return normalizeBendedTrackShape(entry);
    })
    .filter((entry) => entry && entry !== "straight");
  return Array.from(new Set(allowed.length ? allowed : DEFAULT_BEND_SETTINGS.allowedAngles));
};

const normalizeBendSettingsForSave = (values = {}) => {
  const bendSource = values?.bendSettings || {};
  const pricingMethod = BENDED_TRACK_PRICING_METHODS.some(
    (entry) => entry.value === bendSource.pricingMethod
  )
    ? bendSource.pricingMethod
    : "none";

  return {
    ...DEFAULT_BEND_SETTINGS,
    ...bendSource,
    enabled: values?.supportsBendedTrack === true || bendSource.enabled === true,
    pricingMethod,
    defaultBendCharge: parseStitchingSettingNumber(bendSource.defaultBendCharge, 0),
    angleCharges: {
      90: parseStitchingSettingNumber(bendSource.angleCharges?.["90"], 0),
      120: parseStitchingSettingNumber(bendSource.angleCharges?.["120"], 0),
      135: parseStitchingSettingNumber(bendSource.angleCharges?.["135"], 0),
      custom: parseStitchingSettingNumber(bendSource.angleCharges?.custom, 0),
    },
    allowedAngles: normalizeAllowedBendShapes(bendSource.allowedAngles),
    notes: String(bendSource.notes || ""),
  };
};

const normalizeStitchingSettingForState = (values = {}) => {
  const supportsBendedTrack = values?.supportsBendedTrack === true;
  const normalizedBendSettings = normalizeBendSettingsForSave({
    ...values,
    supportsBendedTrack,
  });
  return {
    rate: parseStitchingSettingNumber(values?.rate, 0),
    fullness: parseStitchingSettingNumber(values?.fullness, Number.NaN),
    trackType: values?.trackType || "",
    trackLengthFt: parseStitchingSettingNumber(
      values?.trackLengthFt ?? values?.trackLength,
      0
    ),
    trackRateFt: parseStitchingSettingNumber(values?.trackRateFt, 0),
    includesTrack: values?.includesTrack === true,
    supportsBendedTrack,
    bendSettings: {
      ...normalizedBendSettings,
      enabled: supportsBendedTrack && normalizedBendSettings.enabled,
    },
  };
};



const normalizeLengthUnit = (value = "in") => {
  const normalized = String(value || "in").trim().toLowerCase();
  if (["in", "inch", "inches"].includes(normalized)) return "in";
  if (["cm", "centimeter", "centimeters"].includes(normalized)) return "cm";
  if (["mm", "millimeter", "millimeters"].includes(normalized)) return "mm";
  if (["ft", "foot", "feet", "rft", "running ft", "runningft"].includes(normalized)) return "ft";
  if (["m", "meter", "meters", "metre", "metres"].includes(normalized)) return "m";
  return "in";
};



const hasLegacyTrackSegmentFeet = (trackConfig = {}) =>
  Array.isArray(trackConfig.segments) &&
  trackConfig.segments.some((segment = {}) => {
    const hasRawLength =
      segment.length !== undefined &&
      segment.length !== null &&
      segment.length !== "";
    return (
      !hasRawLength &&
      segment.lengthFt !== undefined &&
      segment.lengthFt !== null &&
      segment.lengthFt !== ""
    );
  });

const getRowMeasurementUnit = (row = {}, quoteUnit = "in") => {
  const trackConfig = row.trackConfig || {};
  return normalizeLengthUnit(
    row.segmentUnit ||
      trackConfig.segmentUnit ||
      trackConfig.totalSegmentLengthUnit ||
      (hasLegacyTrackSegmentFeet(trackConfig) ? "ft" : "") ||
      row.measurementUnit ||
      row.dimensionUnit ||
      row.unit ||
      quoteUnit ||
      "in"
  );
};

const convertLengthToFeet = (value, sourceUnit = "in") => {
  const amount = Math.max(0, num(value));
  const normalizedUnit = normalizeLengthUnit(sourceUnit);
  if (normalizedUnit === "ft") return round2(amount);
  if (normalizedUnit === "cm") return round2(amount / 30.48);
  if (normalizedUnit === "mm") return round2(amount / 304.8);
  if (normalizedUnit === "m") return round2(amount * 3.28084);
  return round2(amount / 12);
};



const BEND_LOCATION_OPTIONS = [
  {
    value: "after_a",
    label: "After Segment A / between A and B",
    locationType: "between_segments",
    afterSegment: "A",
    betweenSegments: ["A", "B"],
    side: null,
  },
  {
    value: "between_b_c",
    label: "Between Segment B and C",
    locationType: "between_segments",
    afterSegment: "B",
    betweenSegments: ["B", "C"],
    side: null,
  },
  {
    value: "left_side",
    label: "Left side bend",
    locationType: "left_side",
    afterSegment: "",
    betweenSegments: [],
    side: "left",
  },
  {
    value: "right_side",
    label: "Right side bend",
    locationType: "right_side",
    afterSegment: "",
    betweenSegments: [],
    side: "right",
  },
  {
    value: "center",
    label: "Center bend",
    locationType: "center",
    afterSegment: "",
    betweenSegments: [],
    side: null,
  },
  {
    value: "custom",
    label: "Custom note",
    locationType: "custom",
    afterSegment: "",
    betweenSegments: [],
    side: null,
  },
];

const getBendLocationOption = (location = {}) => {
  if (location.locationType === "left_side") return "left_side";
  if (location.locationType === "right_side") return "right_side";
  if (location.locationType === "center") return "center";
  if (location.locationType === "custom") return "custom";
  const between = Array.isArray(location.betweenSegments)
    ? location.betweenSegments.join("-")
    : "";
  if (between === "B-C") return "between_b_c";
  return "after_a";
};

const buildBendLocationFromOption = (
  optionValue = "after_a",
  bendNo = 1,
  angleDeg = null,
  distanceUnit = "in",
  previous = {}
) => {
  const option =
    BEND_LOCATION_OPTIONS.find((entry) => entry.value === optionValue) ||
    BEND_LOCATION_OPTIONS[0];
  return {
    bendNo,
    angleDeg: angleDeg == null ? null : Number(angleDeg),
    locationType: option.locationType,
    afterSegment: option.afterSegment,
    betweenSegments: [...option.betweenSegments],
    side: option.side,
    distanceFromStart: previous.distanceFromStart ?? null,
    distanceUnit: normalizeLengthUnit(previous.distanceUnit || distanceUnit),
    note: previous.note || "",
  };
};

const getDefaultBendLocationOption = (shape = "straight", bendNo = 1) => {
  const normalizedShape = normalizeBendedTrackShape(shape);
  if (normalizedShape === "u_shape" && Number(bendNo) === 2) return "between_b_c";
  return "after_a";
};

const normalizeBendLocations = (
  bendLocations = [],
  { shape = "straight", bendCount = 0, angleDeg = null, unit = "in" } = {}
) => {
  const source = Array.isArray(bendLocations) ? bendLocations : [];
  const count = Math.max(0, Math.round(num(bendCount)));
  return Array.from({ length: count }).map((_, index) => {
    const bendNo = index + 1;
    const previous = source[index] || {};
    const option = previous.locationType
      ? getBendLocationOption(previous)
      : getDefaultBendLocationOption(shape, bendNo);
    return {
      ...buildBendLocationFromOption(option, bendNo, previous.angleDeg ?? angleDeg, unit, previous),
      angleDeg:
        previous.angleDeg == null || previous.angleDeg === ""
          ? angleDeg == null
            ? null
            : Number(angleDeg)
          : Number(previous.angleDeg),
    };
  });
};

const summarizeBendLocation = (location = {}) => {
  if (location.locationType === "left_side") return "Left side bend";
  if (location.locationType === "right_side") return "Right side bend";
  if (location.locationType === "center") return "Center bend";
  if (location.locationType === "custom") return location.note || "Custom bend location";
  const between = Array.isArray(location.betweenSegments)
    ? location.betweenSegments.filter(Boolean)
    : [];
  if (between.length >= 2) {
    return `After Segment ${between[0]} / between ${between[0]} and ${between[1]}`;
  }
  if (location.afterSegment) return `After Segment ${location.afterSegment}`;
  return "";
};

const summarizeBendLocations = (locations = []) =>
  (Array.isArray(locations) ? locations : [])
    .map(summarizeBendLocation)
    .filter(Boolean)
    .join("; ");

const normalizeTrackSegments = (segments = [], defaultUnit = "in") => {
  const source = Array.isArray(segments) ? segments : [];
  const fallbackUnit = normalizeLengthUnit(defaultUnit);
  return ["A", "B", "C"].map((label, index) => {
    const sourceSegment = source[index] || {};
    const hasRawLength =
      sourceSegment.length !== undefined &&
      sourceSegment.length !== null &&
      sourceSegment.length !== "";
    const segmentUnit = hasRawLength
      ? normalizeLengthUnit(sourceSegment.unit || sourceSegment.segmentUnit || fallbackUnit)
      : sourceSegment.lengthFt !== undefined && sourceSegment.lengthFt !== null
      ? "ft"
      : fallbackUnit;
    const length = Math.max(
      0,
      num(hasRawLength ? sourceSegment.length : sourceSegment.lengthFt ?? 0)
    );

    return {
      label: sourceSegment.label || label,
      length,
      unit: segmentUnit,
      lengthFt: convertLengthToFeet(length, segmentUnit),
    };
  });
};



const getBendedTrackShapeMeta = (shape = "straight") =>
  BENDED_TRACK_SHAPE_MAP[normalizeBendedTrackShape(shape)] || BENDED_TRACK_SHAPE_MAP.straight;

const calculateCurtainTrackConfig = (
  row = {},
  selectedStitchingTrack = {},
  { baseTrackLengthFt = 0, measurementUnit = "in" } = {}
) => {
  const packageConfig = normalizeStitchingSettingForState(selectedStitchingTrack || {});
  const trackConfig = row?.trackConfig || {};
  const shape = normalizeBendedTrackShape(trackConfig.shape || "straight");
  const shapeMeta = getBendedTrackShapeMeta(shape);
  const segmentUnit = getRowMeasurementUnit(
    {
      ...row,
      segmentUnit: trackConfig.segmentUnit,
      trackConfig,
    },
    measurementUnit
  );
  const supportsBendedTrack = packageConfig.supportsBendedTrack === true;
  const includesTrack = packageConfig.includesTrack === true || supportsBendedTrack;
  const isBended =
    supportsBendedTrack &&
    shape !== "straight" &&
    (trackConfig.isBended === true || shape !== "straight");
  const bendSettings = packageConfig.bendSettings || DEFAULT_BEND_SETTINGS;
  const pricingMethod = supportsBendedTrack
    ? bendSettings.pricingMethod || "none"
    : "none";
  const bendCount = isBended
    ? Math.max(0, num(trackConfig.bendCount ?? shapeMeta.defaultBendCount ?? 1))
    : 0;
  const angleDeg =
    shape === "custom"
      ? num(trackConfig.angleDeg || 0) || null
      : shapeMeta.defaultAngleDeg ?? (trackConfig.angleDeg == null ? null : num(trackConfig.angleDeg));
  const angleKey = shapeMeta.angleKey || "custom";
  const autoBendCharge =
    pricingMethod === "fixed_per_bend"
      ? num(bendSettings.defaultBendCharge)
      : pricingMethod === "by_angle"
      ? num(bendSettings.angleCharges?.[angleKey] ?? bendSettings.angleCharges?.custom)
      : 0;
  const manualBendCharge =
    trackConfig.manualBendCharge === "" || trackConfig.manualBendCharge == null
      ? null
      : Math.max(0, num(trackConfig.manualBendCharge));
  const bendChargePerBend =
    isBended && pricingMethod === "manual"
      ? Number(manualBendCharge || 0)
      : isBended
      ? Math.max(0, autoBendCharge)
      : 0;
  const bendAmount = round2(bendCount * bendChargePerBend);
  const segments = normalizeTrackSegments(trackConfig.segments, segmentUnit);
  const totalSegmentLength = round2(
    segments.reduce((sum, segment) => sum + Number(segment.length || 0), 0)
  );
  const segmentTotalFt = convertLengthToFeet(totalSegmentLength, segmentUnit);
  const hasSegmentOverride = totalSegmentLength > 0;
  const totalTrackLengthFt = segmentTotalFt > 0 ? segmentTotalFt : round2(num(baseTrackLengthFt));
  const bendLocations = normalizeBendLocations(trackConfig.bendLocations, {
    shape,
    bendCount,
    angleDeg,
    unit: segmentUnit,
  });
  const bendLocationSummary = summarizeBendLocations(bendLocations);

  return {
    ...DEFAULT_TRACK_CONFIG,
    ...trackConfig,
    shape,
    isBended,
    angleDeg,
    bendCount,
    bendChargePerBend,
    bendAmount,
    pricingMethod,
    manualBendCharge,
    notes: String(trackConfig.notes || ""),
    segmentUnit,
    segments,
    totalSegmentLength,
    totalSegmentLengthUnit: segmentUnit,
    hasSegmentOverride,
    totalTrackLengthFt,
    bendLocations,
    bendLocationSummary,
    includesTrack,
    supportsBendedTrack,
    disabledReason:
      trackConfig.isBended === true && !supportsBendedTrack
        ? "Bended track disabled because selected package does not support it."
      : "",
  };
};



const applyBendedTrackToCalcResult = (
  row = {},
  calc = {},
  selectedStitchingTrack = {},
  measurementUnit = "in"
) => {
  const baseTrackLengthFt =
    num(row.trackLengthFt ?? row.trackLength) ||
    num(selectedStitchingTrack?.trackLengthFt ?? selectedStitchingTrack?.trackLength);
  const trackConfig = calculateCurtainTrackConfig(row, selectedStitchingTrack, {
    baseTrackLengthFt,
    measurementUnit: getRowMeasurementUnit(row, measurementUnit),
  });
  const trackRateFt = num(selectedStitchingTrack?.trackRateFt);
  const hasSegmentOverride = trackConfig.totalTrackLengthFt > 0 && trackConfig.totalTrackLengthFt !== baseTrackLengthFt;
  const trackLengthAdjustment =
    hasSegmentOverride && trackRateFt > 0 && trackConfig.includesTrack && row.includeTrack !== false
      ? round2((trackConfig.totalTrackLengthFt - baseTrackLengthFt) * trackRateFt)
      : 0;
  const bendAmount = round2(Number(trackConfig.bendAmount || 0));
  const totalDelta = round2(trackLengthAdjustment + bendAmount);
  const discountScope = calc.discountScope || "fabric_only";
  const discountPercent = Math.min(
    100,
    Math.max(0, num(calc.fabricDiscountPercent ?? row.discount))
  );
  const baseLineAmountBeforeDiscount =
    discountScope === "line_total"
      ? round2(
          Number(
            calc.baseLineAmountBeforeDiscount ??
              Number(calc.lineTotal ?? calc.rowTotal ?? 0) + Number(calc.discountAmount || 0)
          ) + totalDelta
        )
      : round2(Number(calc.baseLineAmountBeforeDiscount ?? calc.lineTotal ?? calc.rowTotal ?? 0) + totalDelta);
  const discountAmount =
    discountScope === "line_total"
      ? round2(baseLineAmountBeforeDiscount * (discountPercent / 100))
      : Number(calc.discountAmount || calc.fabricDiscountAmount || 0);
  const lineTotal =
    discountScope === "line_total"
      ? round2(Math.max(0, baseLineAmountBeforeDiscount - discountAmount))
      : round2(Number(calc.lineTotal ?? calc.rowTotal ?? 0) + totalDelta);
  const gstRate = num(calc.gstRate);
  const gstAmount = round2((lineTotal * gstRate) / 100);

  return {
    ...calc,
    trackConfig,
    bendAmount,
    trackCost: round2(Number(calc.trackCost || 0) + trackLengthAdjustment),
    baseLineAmountBeforeDiscount,
    discountScope,
    discountAmount,
    discountBase:
      discountScope === "line_total"
        ? baseLineAmountBeforeDiscount
        : Number(calc.discountBase || calc.fabricGrossAmount || 0),
    lineTotal,
    rowTotal: lineTotal,
    taxableAmount: lineTotal,
    gstAmount,
    total: round2(lineTotal + gstAmount),
  };

};

export {
  DEFAULT_STITCHING, DEFAULT_CURTAIN_LINING, CURTAIN_LINING_TYPES,
  normalizeCurtainRowLining, calculateCurtainRowLining,
  calculateCurtainTrackConfig, applyBendedTrackToCalcResult,
  normalizeStitchingSettingForState,
};

export const DEFAULT_HALF_PIECE_REUSE_SETTINGS = {
  heightMatchMode: "can_trim_down",
  heightToleranceInches: 1,
  allowLargerPieceToBeTrimmed: true,
};
const HALF_PIECE_EPSILON = 0.000001;

// Product snapshots here are caller-authorized DB settings, not submitted
// calculation artifacts. The caller must replace/remove untrusted snapshots.
function calculationInput(row) {
  const clean = { ...row };
  for (const key of Object.keys(clean)) {
    if ((/snapshot/i.test(key) && key !== "productConfigSnapshot") || key.startsWith("__")) delete clean[key];
  }
  for (const key of ["sqftCalculation", "billableSqft", "adjSqft", "sqftAmount",
    "rowTotal", "total", "calculationBreakdown", "fabricRules", "sqftRules",
    "calculationType", "lineType", "category", "usesFabric", "supportsLining",
    "isCurtain", "isRoman", "isBlind", "isAccessory"]) delete clean[key];
  return clean;
}

export function supportsHalfPieceReuse(row = {}, productConfig = null) {
  const name = normalizeProductName(productConfig?.name || row.productName || row.productType || row.product || "");
  const category = normalizeProductName(productConfig?.category || "");
  const formula = productConfig?.fabricRules?.meterFormulaType;
  if (name.startsWith("roman") || category === "roman_blind" || formula === "roman_panels") {
    return productConfig?.fabricRules?.allowAsymmetricalRomanJoin === true ||
      productConfig?.workshopRules?.allowRomanHalfPieceReuse === true;
  }
  return (name === "curtains" || category === "curtain" || formula === "curtain_panels") &&
    productConfig?.workshopRules?.allowHalfPieceReuse !== false;
}

export function isHalfPieceLeftoverCompatible(leftover = {}, requiredLengthM = 0, settings = {}) {
  const toleranceM = Math.max(0, Number(settings.heightToleranceInches || 0)) / 39.37;
  const leftoverLengthM = Number(leftover.length || 0);
  const requiredM = Number(requiredLengthM || 0);
  if (settings.heightMatchMode === "exact" || settings.heightMatchMode === "tolerance" ||
      settings.allowLargerPieceToBeTrimmed === false) {
    return Math.abs(leftoverLengthM - requiredM) <= toleranceM + HALF_PIECE_EPSILON;
  }
  return leftoverLengthM + toleranceM + HALF_PIECE_EPSILON >= requiredM;
}

function buildPiecePlan(calc) {
  const piece = calc.calculationBreakdown?.curtainPieceCalc;
  const raw = Number(piece?.rawPieces || 0);
  if (calc.calculationBreakdown?.fabricRules?.meterFormulaType === "simple_length") {
    // Explicit length products have whole cuts, not curtain half-panel geometry.
    const whole = Number(calc.calculationBreakdown.panels || 0);
    return { raw: whole, whole, half: whole, floor: whole, hasHalf: false };
  }
  // Balanced Roman panels never produce a half-panel requirement, even when
  // the product permits reuse. Do not reinterpret rawPanels as curtain panels.
  if (calc.isRoman) {
    const whole = Number(calc.romanPanelCalc?.totalPanels || calc.totalPieces || 0);
    return { raw: Number(calc.romanPanelCalc?.rawPanels || 0) * calc.quantity, whole, half: whole, floor: whole, hasHalf: false };
  }
  const whole = raw ? Math.ceil(Math.max(0, raw - HALF_PIECE_EPSILON)) : Number(piece?.pieces || 0);
  const half = raw ? Math.ceil(Math.max(0, raw - HALF_PIECE_EPSILON) * 2) / 2 : whole;
  const floor = Math.floor(half + HALF_PIECE_EPSILON);
  return { raw, whole, half, floor, hasHalf: round2(Math.max(0, half - floor)) >= 0.5 - HALF_PIECE_EPSILON };
}

function applyFabricBilling(row, calc, plan, passSavings) {
  const customerBillableFabricMeters = passSavings && plan.reusedHalfCount > 0 ? plan.newMeters : plan.fullMeters;
  const discountPercent = Math.min(100, num(row.discount));
  const mrpPerMeter = num(row.mrp || row.mrpPerMeter);
  // Preserve the app's multiplication order: net is rounded independently
  // from gross and discount, rather than obtained by subtracting rounded money.
  const fabricCost = round2(customerBillableFabricMeters * mrpPerMeter * (1 - discountPercent / 100));
  const fabricGrossAmount = round2(customerBillableFabricMeters * mrpPerMeter);
  const fabricDiscountAmount = round2(fabricGrossAmount * (discountPercent / 100));
  const lining = calculateCurtainRowLining(row, customerBillableFabricMeters,
    plan.supportsLining, { sqft: calc.adjSqft ?? calc.billableSqft ?? 0 });
  const rowTotal = round2(Number(calc.rowTotal || 0) - Number(calc.fabricCost || 0) + fabricCost + Number(lining.amount || 0));
  return {
    ...calc, lining, liningAmount: Number(lining.amount || 0),
    fabricMeters: customerBillableFabricMeters, orderedFabricMeters: customerBillableFabricMeters,
    customerBillableFabricMeters, fullOrderedFabricMeters: plan.fullMeters,
    noReuseOrderedFabricMeters: plan.fullMeters,
    workshopNewFabricMeters: plan.newMeters, workshopFabricRequiredMeters: plan.newMeters,
    internalNewFabricNeededMeters: plan.newMeters, newFabricNeededMeters: plan.newMeters,
    cutFabricMeters: plan.cutMeters, halfPiecesReused: plan.reusedHalfCount,
    halfPiecesSaved: plan.createdHalf ? 1 : 0,
    halfReusedMeters: round2(plan.reusedHalfCount * plan.drop),
    internalHalfReusedMeters: round2(plan.reusedHalfCount * plan.drop),
    internalHalfPieceSavingMeters: round2(Math.max(0, plan.fullMeters - plan.newMeters)),
    internalHalfPieceSavingValue: round2(Math.max(0, plan.fullMeters - plan.newMeters) * mrpPerMeter * (1 - discountPercent / 100)),
    rawPanels: plan.raw, wholeBillingPanels: plan.whole, halfStepProductionPanels: plan.half,
    workshopPanels: plan.newFullCuts + plan.reusedHalfCount * 0.5,
    customerBillingPanels: passSavings && plan.reusedHalfCount > 0 ? plan.newFullCuts + plan.reusedHalfCount * 0.5 : plan.whole,
    totalPieces: passSavings && plan.reusedHalfCount > 0 ? plan.newFullCuts + plan.reusedHalfCount * 0.5 : plan.whole,
    pcsFull: plan.whole, pcsHalf: plan.half, newFullCuts: plan.newFullCuts,
    passHalfPieceSavingsToCustomer: passSavings,
    fabricCost, fabricGrossAmount, fabricDiscountPercent: discountPercent,
    fabricDiscountAmount, fabricNetAmount: fabricCost,
    discountScope: "fabric_only", discountBase: fabricGrossAmount, discountAmount: fabricDiscountAmount,
    baseLineAmountBeforeDiscount: round2(fabricGrossAmount + Number(calc.sqftAmount || calc.productCharge || 0) +
      Number(calc.stitchingCost || 0) + Number(calc.trackCost || 0) + Number(calc.bendAmount || 0) + Number(lining.amount || 0)),
    rowTotal, lineTotal: rowTotal, rowSubtotalBeforeGst: rowTotal, taxableAmount: rowTotal,
    gstAmount: round2(rowTotal * num(calc.gstRate) / 100),
    total: round2(rowTotal + round2(rowTotal * num(calc.gstRate) / 100)),
  };
}

/** Pure live calculation. Settings arguments, row.productConfigSnapshot and
 * quote.quoteCalculationSettingsSnapshot must be authorized by the caller
 * (loaded from DB or verified/rebuilt server-side), never passed from a client
 * unchecked. Product snapshots take precedence over current product defaults.
 * Submitted calculation snapshots and derived values are not pricing authority.
 * Dimensions, rates, manual amounts and explicit quote overrides remain inputs.
 */
export function calculateCurtainFinancials({
  rows = [], quote = {}, productConfigs = [], stitchingRates = DEFAULT_STITCHING,
  fabricCalcSettings = {}, minSqft = {}, getFabricKey = getQuoteFabricIdentity,
  legacyRepeatCompatibility = false,
} = {}) {
  const configs = productConfigs.map(normalizeProductConfig);
  const cleanRows = rows.map(calculationInput);
  const settings = {
    stitchingRates, fabricCalcSettings, minSqft,
    legacyRepeatCompatibility,
    quoteCalculationSettingsSnapshot: quote.quoteCalculationSettingsSnapshot || {},
    quoteFullnessOverrides: quote.quoteFullnessOverrides || {},
    fabricHemOverrides: quote.fabricHemOverrides || {},
    quoteTrackRateOverrides: quote.quoteTrackRateOverrides || {},
    repeatSettings: quote.repeatSettings || {},
    blindSqftRoundingMode: quote.calculationSettings?.blindSqftRoundingMode ?? quote.blindSqftRoundingMode ?? fabricCalcSettings.blindSqftRoundingMode,
    gstRate: quote.quoteGstRate ?? quote.gstRate ?? 0,
  };
  const entries = cleanRows.map((row, index) => {
    const product = row.productConfigSnapshot
      ? normalizeProductConfig(row.productConfigSnapshot)
      : row.productId
      ? configs.find(config => config.id === row.productId)
      : configs.find(config => normalizeProductName(config.name) === normalizeProductName(row.productName || row.productType || row.product || ""));
    if (!product) throw new Error(`Unknown authorized product for row ${index}`);
    // Canonical metadata prevents derived client classification changing tax or repeat scope.
    const input = { ...row, productId: product.id, productName: product.name, productType: product.name,
      category: product.category, calculationType: product.calculationType };
    const rowSettings = { ...settings, fabricKeyForRepeat: getFabricKey(input, index) };
    let calc = calculateProductRow(input, product, rowSettings);
    const selected = resolveEffectiveQuoteCalculationSettings({ row: input, productConfig: product, quote: rowSettings }).selectedStitchingStyle;
    if (!calc.isAccessory) calc = applyBendedTrackToCalcResult(input, calc, selected.config, quote.unit || "in");
    return { row: input, product, calc, index };
  });
  const pool = [];
  const reuseSettings = { ...DEFAULT_HALF_PIECE_REUSE_SETTINGS, ...quote.halfPieceReuseSettings };
  const calculations = entries.map(entry => entry.calc);
  for (const entry of [...entries].sort((a, b) => Number(b.calc.cutCurtain || b.calc.cutRoman || 0) - Number(a.calc.cutCurtain || a.calc.cutRoman || 0))) {
    const { row, product, calc, index } = entry;
    const supportsLining = productSupportsLining(product, row);
    if (!calc.isCurtain && !calc.isRoman && !supportsLining) {
      calculations[index] = { ...calc, lining: calculateCurtainRowLining(row, calc.fabricMeters, false), liningAmount: 0,
        customerBillableFabricMeters: calc.fabricMeters, workshopNewFabricMeters: calc.fabricMeters, cutFabricMeters: calc.fabricMeters };
      continue;
    }
    const piece = buildPiecePlan(calc);
    const drop = cuttingDropMetres(Number(calc.cutCurtain || calc.cutRoman || 0), calc.repeatSettings, legacyRepeatCompatibility);
    if (legacyRepeatCompatibility && calc.repeatCuttingVersion) calc.repeatCuttingVersion = 'legacy-repeat-v1';
    const width = round2(Number(calc.calculationBreakdown?.fabricRules?.fabricWidthIn || row.fabricWidthIn || 0));
    const fabric = getQuoteFabricIdentity(row);
    const hasFabric = JSON.parse(fabric).some(Boolean);
    let newFullCuts = piece.whole, reusedHalfCount = 0, createdHalf = false;
    if (piece.hasHalf && hasFabric && drop > 0 && quote.useHalfPieceReuse !== false && supportsHalfPieceReuse(row, product)) {
      const compatible = pool.filter(leftover => leftover.fabric === fabric && leftover.width === width &&
        isHalfPieceLeftoverCompatible(leftover, drop, reuseSettings)).sort((a, b) => a.length - b.length || a.index - b.index)[0];
      if (compatible) {
        pool.splice(pool.indexOf(compatible), 1);
        newFullCuts = piece.floor;
        reusedHalfCount = 1;
      } else {
        pool.push({ fabric, width, length: drop, index });
        createdHalf = true;
      }
    }
    calculations[index] = applyFabricBilling(row, calc, {
      ...piece, drop, newFullCuts, reusedHalfCount, createdHalf, supportsLining,
      fullMeters: round2(piece.whole * drop), newMeters: round2(newFullCuts * drop), cutMeters: round2(piece.half * drop),
    }, quote.passHalfPieceSavingsToCustomer === true);
  }
  const subtotal = round2(calculations.reduce((sum, calc) => sum + Number(calc.rowTotal || 0), 0));
  const totals = calculateCurtainQuoteTotals({
    rows: entries.map(({ row, product }) => ({ ...row, productConfigSnapshot: product })),
    subtotal, discountType: quote.discountType ?? (quote.summaryDiscountMode === "percent" ? "percent" : "flat"),
    discountValue: quote.discountValue ?? quote.summaryDiscountValue ?? 0,
    quoteGstMode: quote.quoteGstMode ?? quote.gstMode,
    quoteGstRate: quote.quoteGstRate ?? quote.gstRate,
    getRowCalculation: (_, index) => calculations[index],
    isAccessoryRow: (_, index) => calculations[index].isAccessory,
  });
  return { calculations, totals, subtotal };
}

/** Build only from a fresh runtime calculation; never spread a submitted snapshot.
 * The caller may add its server timestamp separately so this helper stays pure.
 */
export function buildAuthoritativeCurtainRowSnapshot(row = {}, calc = {}) {
  const piece = calc.calculationBreakdown?.curtainPieceCalc || {};
  const rules = calc.calculationBreakdown?.fabricRules || {};
  const fullness = calc.calculationBreakdown?.fullness || {};
  const repeat = calc.repeatSettings || {};
  const repeatSettings = Object.fromEntries([
    "verticalEnabled", "verticalRepeatValue", "verticalRepeatUnit", "verticalRepeatIn", "verticalRepeatInch",
    "horizontalEnabled", "horizontalRepeatValue", "horizontalRepeatUnit", "horizontalRepeatIn", "horizontalRepeatInch",
    "applyMode", "productTypeScope",
  ].filter(key => repeat[key] !== undefined).map(key => [key, repeat[key]]));
  const effectiveFullness = Number(fullness.fullness || piece.fullness || 0);
  const defaultFullness = Number(fullness.stitchingFullness || fullness.productDefaultFullness || fullness.productFullness || 0);
  const snapshot = {
    calculationVersion: CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION,
    productId: calc.productId || "", productName: calc.productName || "",
    fabricWidthIn: Number(piece.fabricWidthIn ?? rules.fabricWidthIn ?? 0),
    usableWidthDeductionIn: Number(piece.usableWidthDeductionIn ?? rules.usableWidthDeductionIn ?? 0),
    usableFabricWidthIn: Number(piece.usableFabricWidthIn ?? rules.usableFabricWidthIn ?? 0),
    fullness: Number(piece.fullness || effectiveFullness), effectiveFullness,
    fullnessSource: fullness.source || "", stitchingKey: fullness.stitchingStyleKey || "",
    stitchingLabel: fullness.stitchingLabel || "", defaultFullness,
    quoteFullness: Number(fullness.quoteFullness || 0) || null,
    widthIn: Number(calc.widthIn || 0), heightIn: Number(calc.heightIn || 0),
    rawSqft: Number(calc.sqftCalculation?.rawSqft ?? calc.rawSqft ?? 0),
    billableSqft: Number(calc.sqftCalculation?.billableSqft ?? calc.billableSqft ?? 0),
    sqftCalculation: calc.sqftCalculation || null,
    rawPieces: Number(piece.rawPieces ?? calc.rawPanels ?? 0),
    pieces: Number(piece.pieces ?? calc.pcsFull ?? calc.totalPieces ?? 0), roundingMode: "ceil",
    cutHeightIn: Number(calc.cutCurtain || calc.cutRoman || 0),
    ...(calc.repeatCuttingVersion ? { repeatCuttingVersion: calc.repeatCuttingVersion, baseCutHeightIn: Number(calc.baseCutHeightIn || 0) } : {}),
    orderedFabricMeters: Number(calc.orderedFabricMeters ?? calc.fabricMeters ?? 0),
    fullOrderedFabricMeters: Number(calc.fullOrderedFabricMeters ?? calc.fabricMetersFull ?? calc.fabricMeters ?? 0),
    customerBillableFabricMeters: Number(calc.customerBillableFabricMeters ?? calc.fabricMeters ?? 0),
    workshopFabricRequiredMeters: Number(calc.workshopFabricRequiredMeters ?? calc.fabricMeters ?? 0),
    workshopNewFabricMeters: Number(calc.workshopNewFabricMeters ?? calc.fabricMeters ?? 0),
    cutFabricMeters: Number(calc.cutFabricMeters ?? calc.fabricMeters ?? 0),
    fabricAmount: Number(calc.fabricCost || 0), fabricCost: Number(calc.fabricCost || 0),
    fabricGrossAmount: Number(calc.fabricGrossAmount || 0),
    fabricDiscountPercent: Number(calc.fabricDiscountPercent || 0),
    fabricDiscountAmount: Number(calc.fabricDiscountAmount || 0),
    fabricNetAmount: Number(calc.fabricNetAmount ?? calc.fabricCost ?? 0),
    discountScope: calc.discountScope || "none", discountBase: Number(calc.discountBase || 0),
    discountAmount: Number(calc.discountAmount || 0),
    baseLineAmountBeforeDiscount: Number(calc.baseLineAmountBeforeDiscount || 0),
    sqftAmount: Number(calc.sqftAmount || calc.productCharge || 0),
    stitchingTrackAmount: round2(Number(calc.stitchingCost || 0) + Number(calc.trackCost || 0) + Number(calc.bendAmount || 0)),
    rowSubtotal: Number(calc.rowTotal || 0), rowDiscount: 0, rowGst: Number(calc.gstAmount || 0),
    rowTotal: Number(calc.rowTotal || 0), lineTotal: Number(calc.lineTotal || 0),
    lining: calc.lining || null, liningAmount: Number(calc.liningAmount || 0),
    bendAmount: Number(calc.bendAmount || 0), trackConfig: calc.trackConfig || null,
    repeatSettings,
    repeatSignature: [
      repeat.verticalEnabled && repeat.verticalRepeatInch > 0 ? `v:${round2(repeat.verticalRepeatInch)}` : "v:off",
      repeat.horizontalEnabled && repeat.horizontalRepeatInch > 0 ? `h:${round2(repeat.horizontalRepeatInch)}` : "h:off",
      `mode:${repeat.applyMode || "all"}`, `scope:${(repeat.productTypeScope || []).join(",")}`,
    ].join("|"),
  };
  snapshot.calculationInputs = {
    fabricWidth: snapshot.fabricWidthIn, usableWidthDeduction: snapshot.usableWidthDeductionIn,
    usableFabricWidth: snapshot.usableFabricWidthIn, fullness: snapshot.fullness, effectiveFullness,
    fullnessSource: snapshot.fullnessSource, stitchingKey: snapshot.stitchingKey,
    stitchingLabel: snapshot.stitchingLabel, defaultFullness, quoteFullness: snapshot.quoteFullness,
    repeatSettings, rate: num(row.mrp || row.mrpPerMeter || row.rate || 0),
    stitchingRate: num(row.customStitchRate || 0), trackLength: num(row.trackLengthFt || row.trackLength || 0),
    gstRate: calc.isAccessory ? num(calc.gstRate) : "",
  };
  return snapshot;
}

